import { useEffect, useMemo, useState } from 'react';
import {
  noteNeedsAttention,
  studentName,
  studentSortName,
  type Student,
  type StudentFlagDef,
} from '@td/shared';
import { useArchivedStudents, useStudents, useUpdateStudent } from '../hooks/useStudents';
import { useStudentFlags } from '../hooks/useStudentFlags';
import { StudentAvatar } from '../components/StudentAvatar';
import { StudentEditor } from '../components/StudentEditor';
import { StudentProfile } from '../components/StudentProfile';
import { FlagBadge } from '../components/FlagBadge';
import { FlagManager } from '../components/FlagManager';
import { PrintRosterMenu, type PrintLayout } from '../components/PrintRosterMenu';
import { usePrivacy } from '../privacy/PrivacyContext';

const VIEWS = ['cards', 'list', 'detailed'] as const;
type RosterView = (typeof VIEWS)[number];

const VIEW_LABELS: Record<RosterView, string> = {
  cards: 'Cards',
  list: 'List',
  detailed: 'Detailed',
};

/**
 * The chosen view sticks and becomes the default next visit. It lives in
 * localStorage rather than on the account because it describes the screen
 * you're looking at — a laptop and a projector want different answers.
 */
const VIEW_KEY = 'td.rosterView';

function readStoredView(): RosterView {
  try {
    const raw = localStorage.getItem(VIEW_KEY);
    return VIEWS.includes(raw as RosterView) ? (raw as RosterView) : 'cards';
  } catch {
    return 'cards';
  }
}

export function RosterPage() {
  const { data: students = [], isLoading, error } = useStudents();
  const { data: allStudents = [] } = useArchivedStudents();
  const { data: flagDefs = [] } = useStudentFlags();
  const updateStudent = useUpdateStudent();
  const { privacy, toggle } = usePrivacy();

  const [view, setView] = useState<RosterView>(readStoredView);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Student | null | undefined>(undefined);
  const [managingFlags, setManagingFlags] = useState(false);
  const [printing, setPrinting] = useState(false);
  // Set only for the duration of a print, so choosing a layout to print
  // doesn't disturb the one you work in.
  const [printLayout, setPrintLayout] = useState<PrintLayout | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      // Storage blocked — the view still works, it just won't be remembered.
    }
  }, [view]);

  // Printing swaps the layout, prints, and puts it back. `afterprint` is what
  // browsers fire once the dialog closes, whether the sheet was sent or not.
  useEffect(() => {
    if (!printLayout) return;

    function done() {
      setPrintLayout(null);
      document.body.classList.remove('print-portrait');
    }

    document.body.classList.add('print-portrait');
    window.addEventListener('afterprint', done);
    // A frame's grace so the swapped layout is painted before the dialog.
    const id = requestAnimationFrame(() => window.print());

    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('afterprint', done);
      document.body.classList.remove('print-portrait');
    };
  }, [printLayout]);

  const active = useMemo(() => students.filter((s) => s.active), [students]);

  /*
   * Archived means two different things, and both belong in this view.
   *
   * A child archived mid-year is still in the current cohort and comes back
   * with the main list. A whole year put away at the end of June does not —
   * the roster query deliberately excludes past years, so those are fetched
   * separately and merged here. Without the second query the Archived view
   * would quietly empty itself every summer.
   */
  const archived = useMemo(() => {
    const seen = new Set<string>();

    return [...students.filter((s) => !s.active), ...allStudents.filter((s) => !s.active)].filter(
      (s) => (seen.has(s.id) ? false : seen.add(s.id)),
    );
  }, [students, allStudents]);

  /** Archived students grouped under the year they left, newest first. */
  const archivedYears = useMemo(() => {
    const byYear = new Map<string, typeof archived>();

    for (const student of archived) {
      const key = student.schoolYear ?? '';
      byYear.set(key, [...(byYear.get(key) ?? []), student]);
    }

    return [...byYear.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [archived]);

  // Bringing back the last archived student used to leave you looking at an
  // empty list with no way out, because the toggle that returns you to the
  // class only rendered while somebody was archived.
  useEffect(() => {
    if (showArchived && archived.length === 0) setShowArchived(false);
  }, [showArchived, archived.length]);

  const visible = useMemo(() => {
    const pool = showArchived ? archived : active;
    const needle = search.trim().toLowerCase();
    const found = needle
      ? pool.filter(
          (s) =>
            studentSortName(s).toLowerCase().includes(needle) ||
            studentName(s).toLowerCase().includes(needle) ||
            String(s.number) === needle,
        )
      : pool;
    // Archived students have no number, so they fall back to name order.
    return [...found].sort((a, b) =>
      a.number != null && b.number != null
        ? a.number - b.number
        : studentSortName(a).localeCompare(studentSortName(b)),
    );
  }, [active, archived, showArchived, search]);

  // Printing always shows the class, never the archived list.
  const shownView = printLayout ?? view;
  const shownStudents = printLayout
    ? [...active].sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
    : visible;

  const open = students.find((s) => s.id === openId) ?? null;
  const followUps = active.reduce((n, s) => n + s.notes.filter(noteNeedsAttention).length, 0);

  return (
    <div className="mx-auto max-w-5xl">
      <header className="page-header flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-2xl">Roster</h1>
          <p className="mt-0.5 text-sm text-stone">
            {students.length === 0
              ? 'Add your class and everything else can point at it.'
              : `${active.length} student${active.length === 1 ? '' : 's'}` +
                (archived.length ? ` · ${archived.length} archived` : '') +
                (followUps
                  ? ` · ${followUps} note${followUps === 1 ? '' : 's'} waiting on follow-up`
                  : '')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <input
            className="field w-40 py-1.5"
            placeholder="Find a student…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search the roster"
          />
          <button
            onClick={toggle}
            aria-pressed={privacy}
            title="Blur photos and hide names (Ctrl+Shift+P)"
            className={privacy ? 'btn-primary' : 'btn-secondary'}
          >
            {privacy ? 'Privacy on' : 'Privacy'}
          </button>
          <button onClick={() => setPrinting(true)} className="btn-secondary">
            Print
          </button>
          <button onClick={() => setEditing(null)} className="btn-primary">
            Add student
          </button>
        </div>
      </header>

      <div className="mb-3 hidden print:block">
        <h1 className="text-xl">
          {printLayout === 'numbers'
            ? 'Class list'
            : `Roster · ${active.length} student${active.length === 1 ? '' : 's'}`}
        </h1>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs print:hidden">
        <div className="flex items-center gap-1" role="group" aria-label="Roster view">
          {VIEWS.map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              aria-pressed={view === v}
              className={[
                'rounded-lg px-2.5 py-1 transition-colors',
                view === v ? 'accent-fill' : 'bg-linen text-walnut hover:bg-sand/60',
              ].join(' ')}
            >
              {VIEW_LABELS[v]}
            </button>
          ))}
        </div>

        <button onClick={() => setManagingFlags(true)} className="btn-soft-accent">
          Edit flags
        </button>

        {(archived.length > 0 || showArchived) && (
          <button
            onClick={() => setShowArchived((v) => !v)}
            aria-pressed={showArchived}
            className={showArchived ? 'text-espresso underline' : 'text-stone hover:text-espresso'}
          >
            {showArchived ? '← Back to the class' : `Archived (${archived.length})`}
          </button>
        )}
      </div>

      {/*
        Which years are in there. A cohort put away in June is not the same
        thing as one child who moved schools in March, and the teacher looking
        for a name from two years ago needs to know both are here.
      */}
      {showArchived && archivedYears.length > 0 && (
        <p className="-mt-2 mb-3 text-xs text-stone">
          {archivedYears
            .map(([year, list]) =>
              year ? `${year}: ${list.length}` : `Archived this year: ${list.length}`,
            )
            .join('  ·  ')}
        </p>
      )}

      {error ? (
        <p className="text-sm text-rust">Could not load the roster.</p>
      ) : students.length === 0 && !isLoading ? (
        <div className="card max-w-lg">
          <p className="text-sm text-walnut">
            No students yet — numbers fill in as you go, and once the class is complete you can
            number everyone alphabetically from Settings.
          </p>
          <button onClick={() => setEditing(null)} className="btn-primary mt-3">
            Add the first student
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="text-sm text-stone">
          <p>
            {search.trim()
              ? `Nobody matches “${search.trim()}”.`
              : showArchived
                ? 'Nobody is archived.'
                : 'Nobody in class right now.'}
          </p>
          {showArchived && (
            <button onClick={() => setShowArchived(false)} className="btn-soft-accent mt-2">
              ← Back to the class
            </button>
          )}
        </div>
      ) : shownView === 'numbers' ? (
        <NumbersView students={shownStudents} />
      ) : shownView === 'cards' ? (
        <CardsView
          students={shownStudents}
          flagDefs={flagDefs}
          loading={isLoading}
          onOpen={setOpenId}
        />
      ) : (
        <ListView
          students={shownStudents}
          flagDefs={flagDefs}
          detailed={shownView === 'detailed'}
          loading={isLoading}
          onOpen={setOpenId}
          onRestore={(id) => updateStudent.mutate({ id, active: true })}
          showArchived={showArchived && !printLayout}
        />
      )}

      {open && (
        <StudentProfile
          student={open}
          onEdit={() => setEditing(open)}
          onClose={() => setOpenId(null)}
        />
      )}

      {editing !== undefined && (
        <StudentEditor
          student={editing}
          roster={students}
          onManageFlags={() => setManagingFlags(true)}
          onClose={() => setEditing(undefined)}
        />
      )}

      {managingFlags && <FlagManager onClose={() => setManagingFlags(false)} />}

      {printing && (
        <PrintRosterMenu
          current={view}
          privacyOn={privacy}
          onPrint={(layout) => {
            setPrinting(false);
            setPrintLayout(layout);
          }}
          onClose={() => setPrinting(false)}
        />
      )}
    </div>
  );
}

/**
 * Numbers and first names at a size you can read from across the room.
 *
 * Meant for the door or the wall, so it deliberately carries nothing else —
 * no photos, no flags, nothing a passing parent shouldn't be reading.
 */
function NumbersView({ students }: { students: Student[] }) {
  // One column up to a full class; only a long roster is worth splitting, and
  // `column-fill: auto` makes a split list read down the page and then across,
  // rather than balancing three short columns side by side.
  const columns = students.length > 22 ? 2 : 1;

  return (
    <ol
      className={columns === 2 ? 'columns-2 gap-10' : ''}
      style={columns === 2 ? { columnFill: 'auto', height: '8.6in' } : undefined}
    >
      {students.map((s) => (
        <li
          key={s.id}
          className="print-keep-together flex items-baseline gap-3 border-b border-sand/70 py-1.5"
        >
          <span className="w-9 shrink-0 text-right text-lg text-stone">{s.number ?? '—'}</span>
          <span className="truncate text-lg text-espresso">
            {studentName(s)} {s.lastName}
          </span>
        </li>
      ))}
    </ol>
  );
}

function FollowUpChip({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span
      title={`${count} note${count === 1 ? '' : 's'} waiting on follow-up`}
      className="rounded bg-ochre/30 px-1 text-[9px] uppercase tracking-wide text-walnut"
    >
      {count} to do
    </span>
  );
}

function CardsView({
  students,
  flagDefs,
  loading,
  onOpen,
}: {
  students: Student[];
  flagDefs: StudentFlagDef[];
  loading: boolean;
  onOpen: (id: string) => void;
}) {
  const { privacy } = usePrivacy();

  return (
    <ul
      className={[
        'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4',
        loading ? 'opacity-50' : '',
      ].join(' ')}
    >
      {students.map((s) => (
        <li key={s.id} className="print-keep-together print-exact">
          <button
            onClick={() => onOpen(s.id)}
            className={[
              'flex w-full flex-col items-center gap-2 rounded-card border bg-white p-3 text-center transition-colors hover:border-clay hover:bg-linen/40',
              s.active ? 'border-sand' : 'border-dashed border-stone/40',
            ].join(' ')}
          >
            <span className="relative">
              <StudentAvatar student={s} size={64} />
              {s.number != null && (
                <span className="absolute -bottom-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-espresso px-1 text-[10px] text-cream">
                  {s.number}
                </span>
              )}
            </span>

            <span className="w-full">
              <span className="block truncate text-sm text-espresso">
                {privacy ? `${studentName(s)[0]}. ${s.lastName[0]}.` : studentName(s)}
              </span>
              <span className="block truncate text-[11px] text-stone">
                {privacy ? '—' : s.lastName}
              </span>
            </span>

            {(s.flags.length > 0 || s.notes.some(noteNeedsAttention)) && (
              <span className="flex flex-wrap justify-center gap-1">
                {s.flags.map((f) => (
                  <FlagBadge key={f} flagKey={f} defs={flagDefs} />
                ))}
                <FollowUpChip count={s.notes.filter(noteNeedsAttention).length} />
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}

function ListView({
  students,
  flagDefs,
  detailed,
  loading,
  onOpen,
  onRestore,
  showArchived,
}: {
  students: Student[];
  flagDefs: StudentFlagDef[];
  detailed: boolean;
  loading: boolean;
  onOpen: (id: string) => void;
  onRestore: (id: string) => void;
  showArchived: boolean;
}) {
  const { privacy } = usePrivacy();

  return (
    <ul
      className={[
        'divide-y divide-sand overflow-hidden rounded-card border border-sand bg-white',
        loading ? 'opacity-50' : '',
      ].join(' ')}
    >
      {students.map((s) => {
        const waiting = s.notes.filter(noteNeedsAttention).length;
        const latest = s.notes[0];

        return (
          <li key={s.id} className="group print-keep-together print-exact">
            <div className="flex items-start gap-3 px-3 py-2 transition-colors hover:bg-linen/40">
              <span className="w-6 shrink-0 pt-1 text-right text-xs text-stone">
                {s.number ?? '—'}
              </span>
              <button
                onClick={() => onOpen(s.id)}
                className="flex min-w-0 flex-1 items-start gap-3 text-left"
              >
                <StudentAvatar student={s} size={detailed ? 44 : 30} />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-sm text-espresso">
                      {privacy
                        ? `${studentName(s)[0]}. ${s.lastName[0]}.`
                        : detailed
                          ? `${studentName(s)} ${s.lastName}`
                          : `${s.lastName}, ${studentName(s)}`}
                    </span>
                    {!privacy && s.preferredName && (
                      <span className="text-[11px] text-stone">{s.firstName} on paper</span>
                    )}
                    <span className="flex flex-wrap gap-1">
                      {s.flags.map((f) => (
                        <FlagBadge key={f} flagKey={f} defs={flagDefs} full={detailed} />
                      ))}
                      <FollowUpChip count={waiting} />
                    </span>
                  </span>

                  {detailed && (
                    <span className="mt-0.5 block space-y-0.5">
                      {s.about.trim() && !privacy && (
                        <span className="block truncate text-[11px] text-walnut">{s.about}</span>
                      )}
                      {latest && !privacy && (
                        <span className="block truncate text-[11px] text-stone">
                          Last note: {latest.body}
                        </span>
                      )}
                      <span className="block text-[10px] text-stone/80">
                        {s.notes.length} note{s.notes.length === 1 ? '' : 's'}
                      </span>
                    </span>
                  )}
                </span>
              </button>

              {showArchived && (
                <button
                  onClick={() => onRestore(s.id)}
                  className="shrink-0 self-center text-xs text-terracotta opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                >
                  Bring back
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
